import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const galleryImages = [
  { file: 'first-day-2026-sisters.jpg',    alt: 'Two smiling young girls with backpacks, dressed in matching striped tops and floral skirts, stand outside the PS 96 entrance on the first day of school' },
  { file: 'first-day-2026-kindergarten.jpg', alt: 'A kindergarten teacher holding a red folder stands hand in hand with a young student in a paper hat and school uniform beside a Class K-11 sign on the school fence' },
  { file: 'first-day-2026-family.jpg',     alt: 'A teacher holding a class sign poses on the sidewalk with a smiling student and her grandmother on the first day of school' },
  { file: 'first-day-2026-teachers.jpg',   alt: 'Two smiling PS 96 teachers stand arm in arm on the sidewalk in front of a yellow school bus on the first day of school' },
  { file: 'classroom-fruit-skewers.jpg',   alt: 'A smiling student wearing purple gloves builds a fruit skewer at a classroom table with a cutting board and plate of fresh fruit, as a classmate works nearby' },
  { file: '20260430_110600.jpg',        alt: 'PS 96 students' },
  { file: '20260430_111829.jpg',        alt: 'PS 96 students' },
  { file: '20260430_121603__1_.jpg',    alt: 'PS 96 students' },
  { file: '20260430_123006.jpg',        alt: 'PS 96 students' },
  { file: '20260202_102800.jpeg',       alt: 'PS 96 students' },
  { file: 'IMG_20260324_123130.jpeg',   alt: 'PS 96 students' },
  { file: 'IMG_20260505_135307__1_.jpeg', alt: 'PS 96 students' },
  { file: 'IMG_20260508_090314__4_.jpeg', alt: 'PS 96 students' },
  { file: 'IMG_20260508_090314__7_.jpeg', alt: 'PS 96 students' },
  { file: '1000013062.jpg',             alt: 'PS 96 students' },
  { file: '1000013063.jpg',             alt: 'PS 96 students' },
  { file: '1000013064.jpg',             alt: 'PS 96 students' },
  { file: '1000013066.jpg',             alt: 'PS 96 students' },
];

const CloseIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const GalleryPage = () => {
  const [lightbox, setLightbox] = useState(null);

  const openLightbox = (index) => setLightbox(index);
  const closeLightbox = () => setLightbox(null);
  const prevImage = () => setLightbox((i) => (i - 1 + galleryImages.length) % galleryImages.length);
  const nextImage = () => setLightbox((i) => (i + 1) % galleryImages.length);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') prevImage();
    if (e.key === 'ArrowRight') nextImage();
  };

  return (
    <div className="min-h-screen bg-white" onKeyDown={handleKeyDown} tabIndex={-1}>
      <Navbar />

      <main id="main-content" className="pt-0">
        {/* Hero */}
        <section
          className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
        >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

          <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                <span className="text-sm font-semibold text-white tracking-tight">Our School</span>
              </div>
            </div>
            <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              Picture Gallery
            </h1>
            <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
              A glimpse into the vibrant life and learning happening every day at PS 96.
            </p>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 pb-24">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdbb2e]/10 rounded-lg border border-[#fdbb2e]/30 mb-4">
              <span className="text-sm font-semibold text-[#00213a]">Life at PS 96</span>
            </div>
            <h2 className="text-4xl font-semibold text-gray-900 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              School Moments
            </h2>
            <p className="text-gray-500 mt-2 text-sm">Click any photo to view full size.</p>
          </div>

          <div className="columns-2 md:columns-3 lg:columns-4 gap-3 space-y-3">
            {galleryImages.map((img, i) => (
              <div
                key={img.file}
                className="break-inside-avoid cursor-pointer overflow-hidden rounded-xl group"
                onClick={() => openLightbox(i)}
                role="button"
                tabIndex={0}
                aria-label={`View photo ${i + 1} of ${galleryImages.length}`}
                onKeyDown={(e) => e.key === 'Enter' && openLightbox(i)}
              >
                <img
                  src={`/assets/images/gallery/${img.file}`}
                  alt={img.alt}
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500 rounded-xl"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Photo lightbox"
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Close lightbox"
          >
            <CloseIcon />
          </button>

          {/* Prev */}
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Previous photo"
          >
            <ChevronLeftIcon />
          </button>

          {/* Image */}
          <img
            src={`/assets/images/gallery/${galleryImages[lightbox].file}`}
            alt={galleryImages[lightbox].alt}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next */}
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Next photo"
          >
            <ChevronRightIcon />
          </button>

          {/* Counter */}
          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            {lightbox + 1} / {galleryImages.length}
          </p>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GalleryPage;
